import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import {
    ActiveOrInactive,
    EpisodeCategory,
    Gender,
    LanguageOptions,
    OrganizationTypeWithLinkedProfessions,
    Profession,
    ServiceCode,
    ServiceType,
    SpecialismCode,
    ThirdPartySsoName,
} from '@common/enums/';
import { MatchingEntityResultCode } from '@common/enums/matchingEntityResultCode';
import type { ISchemaContainer } from './schemaContainer';
import { calculateBase64Length } from '../utils/calculateBase64Length';
import { replaceKoiError } from './replaceKoiError';
import { emailSchema } from './schemas/emailSchema';
import {
    agbCodeSchema,
    identifierRegistrationBigBackendSchema,
    identifierRegistrationBigFrontendSchema,
    identifierRegistrationKpSchema,
} from './schemas/identifierDeclarationSchemas';
import { phoneNumberSchema } from './schemas/phoneNumberSchema';
import { relationNumberExistsSchema } from './schemas/relationNumberSchema';
import {
    specialismCodeValidator,
    validateIdentifierDeclarationField,
    validateIdentifierDeclarationPractitionerIdField,
    validateIdentifierRegistrationBigField,
    validateIdentifierRegistrationKpField,
    validateZorgmailNummerField,
} from './utils/accountFieldsCustomValidationFunctions';
import {
    identifierDeclarationRequired,
    identifierRegistrationBigAllowed,
} from './utils/accountFieldsRequirementsByProfession';
import { replaceErrorPath } from './utils/replaceErrorPath';

const practitionerEmailValidation = Koi.when('authorization.canUpdateProperties', {
    is: Koi.array().items(Koi.string().valid('emailAddress').required(), Koi.string()),
    otherwise: Koi.forbidden(),
    then: emailSchema,
}).options({ convert: true });

export interface IGeneralSettingsValidated {
    authorization: {
        canUpdateProperties: string[];
    };
    gender: Gender;
    mobileNumber: string | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

const practitionerUserNameValidation = Koi.string()
    .min(4)
    .max(255)
    .regex(/^[a-zA-Z0-9.@-_]+$/)
    .error((errors: Joi.ErrorReport[]) => replaceKoiError(errors, 'string.pattern.base', 'userNameRegex'));

const generalSettings = Koi.object().keys({
    authorization: Koi.object()
        .keys({
            canUpdateProperties: Koi.array().required(),
        })
        .required(),
    gender: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('gender').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.koi().enum(Gender),
    }),
    mobileNumber: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('mobileNumber').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: phoneNumberSchema.allow(null),
    }),
    nameFamily: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('nameFamily').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.string().max(255),
    }),
    nameGiven: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('nameGiven').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.string().max(255).allow(null),
    }),
    nameInfix: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('nameInfix').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.string().max(255).allow(null),
    }),
});

export const generalSettingsSchema: ISchemaContainer<IGeneralSettingsValidated> = {
    schema: generalSettings.options({ convert: false, presence: 'required' }),
};

export interface IKsyosAccountValidated {
    authorization: {
        canUpdate: boolean;
        canUpdateProperties: string[];
    };
    emailAddress: string;
    isActivated: boolean;
    userName: string;
}

const ksyosAccount = Koi.object().keys({
    authorization: Koi.object()
        .keys({
            canUpdate: Koi.boolean().required(),
            canUpdateProperties: Koi.array().required(),
        })
        .required(),
    emailAddress: practitionerEmailValidation,
    isActivated: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('isActivated').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.boolean(),
    }),
    userName: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('userName').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: practitionerUserNameValidation,
    }).options({ convert: true }),
});

const ksyosAccountSimpleUserNameValidation = ksyosAccount.keys({
    userName: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('userName').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.string().max(255),
    }),
});

export const ksyosAccountSchema: ISchemaContainer<IKsyosAccountValidated> = {
    schema: ksyosAccount.options({ convert: false, presence: 'required' }),
};

export const ksyosAccountForExistingUserNameSchema: ISchemaContainer<IKsyosAccountValidated> = {
    schema: ksyosAccountSimpleUserNameValidation.options({ convert: false, presence: 'required' }),
};

export interface IRegistrationCodesValidated {
    hasDeclarableQualification?: boolean | null;
    identifierDeclaration: string | null;
    identifierRegistrationBig: string | null;
    identifierRegistrationKp: string | null;
    professions?: Profession[] | null;
}

export interface IRegistrationCodesProfessionsValidated extends IRegistrationCodesValidated {
    professions: Profession[];
}

export interface IPractitionerIsActivated {
    isActivated: boolean;
}

const activatedState = Koi.object().keys({
    isActivated: Koi.boolean(),
});

export const activatedStateSchema: ISchemaContainer<IPractitionerIsActivated> = {
    schema: activatedState.options({ convert: false, presence: 'required' }),
};

const professions = Koi.array().items(Koi.koi().enum(Profession));

const identifierRegistrationCodesBase = Koi.object().keys({
    identifierDeclaration: Koi.when('professions', {
        is: Koi.array().items(
            Koi.string()
                .valid(...identifierDeclarationRequired)
                .required(),
            Koi.string()
        ),
        otherwise: agbCodeSchema.allow(null),
        then: agbCodeSchema,
    }),
    identifierRegistrationKp: identifierRegistrationKpSchema.allow(null),
    professions: professions.optional().allow(null),
});

const identifierRegistrationCodesBackend = Koi.object()
    .keys({
        identifierRegistrationBig: Koi.when('professions', {
            is: Koi.array()
                .has(Koi.valid(...identifierRegistrationBigAllowed))
                .required(),
            otherwise: identifierRegistrationBigBackendSchema.allow(null),
            then: identifierRegistrationBigBackendSchema,
        }),
    })
    .concat(identifierRegistrationCodesBase);

const identifierRegistrationCodesFrontend = Koi.object()
    .keys({
        identifierRegistrationBig: Koi.when('professions', {
            is: Koi.array()
                .has(Koi.valid(...identifierRegistrationBigAllowed))
                .required(),
            otherwise: identifierRegistrationBigFrontendSchema.allow(null),
            then: identifierRegistrationBigFrontendSchema,
        }),
    })
    .concat(identifierRegistrationCodesBase);

const optionalPractitionerRegistrationCodes = Koi.object().keys({
    identifierDeclaration: agbCodeSchema.allow(null),
    identifierRegistrationBig: identifierRegistrationBigFrontendSchema.allow(null),
    identifierRegistrationKp: identifierRegistrationKpSchema.allow(null),
});

const declarableQualificationValidation = Koi.object().keys({
    hasDeclarableQualification: Koi.when('identifierDeclaration', {
        is: Koi.string().regex(/^84[0-9]*$/),
        otherwise: Koi.boolean().allow(null).optional().disallow(true),
        then: Koi.boolean(),
    }),
});

const registrationCodesBackend = declarableQualificationValidation.concat(identifierRegistrationCodesBackend);
const registrationCodesFrontend = declarableQualificationValidation.concat(identifierRegistrationCodesFrontend);

export const registrationCodesFrontendSchema: ISchemaContainer<IRegistrationCodesProfessionsValidated> = {
    schema: registrationCodesFrontend.options({ convert: false, presence: 'required' }),
};

export const registrationCodesBackendSchema: ISchemaContainer<IRegistrationCodesProfessionsValidated> = {
    schema: registrationCodesBackend.options({ convert: false, presence: 'required' }),
};

export interface IIdentifierRegistrationCodes {
    hasDeclarableQualification: boolean | null;
    identifierDeclaration: string | null;
    identifierRegistrationBig: string | null;
    identifierRegistrationKp?: string | null;
}

export interface PractitionerAddValidated {
    emailAddress: string | null;
    gender: Gender;
    identifierDeclaration: string | null;
    identifierRegistrationBig: string | null;
    identifierRegistrationKp: string | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

export interface PractitionerAddValidatedBackend extends PractitionerAddValidated {
    authorization: {
        canInsertProperties: string[];
    };
}

export interface EmploymentAddValidated {
    identifierDeclarationPractitionerId: string | null;
    isOrganizationManager: boolean;
    profession: Profession;
    specialismCode: string | null;
    startDate: Date | string;
    zorgmailNummer: string | null;
}

export interface EmploymentAddBackendValidated extends EmploymentAddValidated {
    emailAddress: string | null;
}

export interface OrganizationPractitionerAddBackendValidated {
    employment: EmploymentAddValidated;
    matchingAccountRequestId: string | null;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitioner: PractitionerAddValidated;
    practitionerWillUseCS: boolean;
}

export interface OrganizationPractitionerAddFrontendValidated extends OrganizationPractitionerAddBackendValidated {
    matchingPractitionerHasEmailAddress: boolean | null;
    matchingResultCode: MatchingEntityResultCode;
}

export interface OrganizationPractitionerValidated {
    employment: {
        identifierDeclarationPractitionerId: string | null;
        profession: Profession;
    };
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitioner: PractitionerAddValidatedBackend;
}

export interface PractitionerRegistrationCodesValidated
    extends Omit<IRegistrationCodesValidated, 'hasDeclarableQualification' | 'professions'> {}

export interface PractitionerEmploymentValidated {
    employment: EmploymentAddBackendValidated;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitionerRegistrationCodes: PractitionerRegistrationCodesValidated;
    practitionerWillUseCS: boolean;
}

export interface PractitionerEmploymentAddValidated extends PractitionerEmploymentValidated {
    organizationId: string;
}

export interface OrganizationEmploymentAddFrontendValidated extends PractitionerEmploymentAddValidated {
    matchingPractitionerHasEmailAddress: boolean | null;
    matchingResultCode: MatchingEntityResultCode;
}

export interface OrganizationEmploymentValidated {
    employment: EmploymentAddValidated;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitioner: PractitionerAddValidated;
}

const employmentAdd = Koi.object().keys({
    identifierDeclarationPractitionerId: Koi.custom(
        (value, helpers) => validateIdentifierDeclarationPractitionerIdField(value, helpers),
        'identifierDeclarationPractitionerId custom validation'
    ),
    isOrganizationManager: Koi.boolean(),
    profession: Koi.koi().enum(Profession),
    specialismCode: Koi.when('profession', {
        is: Profession.medicalSpecialist,
        otherwise: specialismCodeValidator,
        then: Koi.koi().enum(SpecialismCode),
    }),
    startDate: Koi.koi().date().required(),
    zorgmailNummer: Koi.custom(
        (value, helpers) => validateZorgmailNummerField(value, helpers),
        'zorgmailNummer custom validation'
    ),
});

const practitionerRegistrationCodesBase = Koi.object().keys({
    identifierDeclaration: Koi.custom(
        (value, helpers) => validateIdentifierDeclarationField(value, helpers),
        'identifierDeclaration custom validation'
    ),
    identifierRegistrationKp: Koi.custom(
        (value, helpers) => validateIdentifierRegistrationKpField(value, helpers),
        'identifierRegistrationKp custom validation'
    ),
});

const practitionerRegistrationCodesFrontend = Koi.object()
    .keys({
        identifierRegistrationBig: Koi.custom(
            (value, helpers) => validateIdentifierRegistrationBigField(value, helpers, true),
            'identifierRegistrationBig custom validation'
        ),
    })
    .concat(practitionerRegistrationCodesBase);

const practitionerRegistrationCodesBackend = Koi.object()
    .keys({
        identifierRegistrationBig: Koi.custom(
            (value, helpers) => validateIdentifierRegistrationBigField(value, helpers, false),
            'identifierRegistrationBig custom validation'
        ),
    })
    .concat(practitionerRegistrationCodesBase);

const practitionerAddBase = Koi.object()
    .keys({
        emailAddress: Koi.when(Koi.ref('/practitionerWillUseCS'), {
            is: true,
            otherwise: Koi.string().empty().allow('', null),
            then: Koi.when('/matchingPractitionerHasEmailAddress', {
                is: true,
                otherwise: emailSchema.required(),
                then: emailSchema.allow(null),
            }),
        }),
        gender: Koi.koi().enum(Gender),
        nameFamily: Koi.string().max(255),
        nameGiven: Koi.string().max(255).allow(null),
        nameInfix: Koi.string().max(255).allow(null),
    })
    .options({ convert: false, presence: 'required' });

const practitionerAddBackend = practitionerAddBase.concat(practitionerRegistrationCodesBackend);

const practitionerAddFrontend = practitionerAddBase.concat(practitionerRegistrationCodesFrontend);

export const organizationPractitionerAddFrontendSchema: ISchemaContainer<OrganizationPractitionerAddFrontendValidated> =
    {
        schema: Koi.object({
            employment: employmentAdd,
            matchingAccountRequestId: Koi.string().uuid().allow(null),
            matchingPractitionerHasEmailAddress: Koi.boolean().allow(null),
            matchingResultCode: Koi.koi().enum(MatchingEntityResultCode),
            organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
            practitioner: practitionerAddFrontend,
            practitionerWillUseCS: Koi.boolean(),
        }).options({ convert: false, presence: 'required' }),
    };

export const organizationEmploymentValidateInsertSchema: ISchemaContainer<OrganizationEmploymentValidated> = {
    schema: Koi.object().keys({
        employment: employmentAdd.concat(
            Koi.object().keys({
                authorization: Koi.object().keys({
                    canInsertProperties: Koi.array(),
                }),
            })
        ),
        organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
        practitioner: practitionerAddBackend,
    }),
};

export const practitionerEmploymentValidateInsertSchema: ISchemaContainer<PractitionerEmploymentValidated> = {
    schema: Koi.object().keys({
        employment: employmentAdd.concat(
            Koi.object().keys({
                authorization: Koi.object().keys({
                    canInsertProperties: Koi.array(),
                }),
                emailAddress: Koi.when('/practitionerWillUseCS', {
                    is: true,
                    otherwise: Koi.string().empty().allow('', null),
                    then: emailSchema.allow(null),
                }),
            })
        ),
        organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
        practitionerRegistrationCodes: practitionerRegistrationCodesBackend,
        practitionerWillUseCS: Koi.boolean().required(),
    }),
};

export const organizationPractitionerValidateInsertSchema: ISchemaContainer<OrganizationPractitionerValidated> = {
    schema: Koi.object().keys({
        employment: Koi.object().keys({
            identifierDeclarationPractitionerId: Koi.string().uuid().allow(null),
            profession: Koi.koi().enum(Profession),
        }),
        organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
        practitioner: practitionerAddBackend
            .concat(
                Koi.object().keys({
                    authorization: Koi.object().keys({
                        canInsertProperties: Koi.array(),
                    }),
                })
            )
            .options({ convert: false, presence: 'required' }),
    }),
};

const duplicatedIdentifierDeclarationAvoidanceSchema = Koi.object().keys({
    identifierDeclaration: Koi.when('...matchingResultCode', {
        is: Koi.valid(MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION),
        otherwise: agbCodeSchema,
        then: Koi.valid(null).error((errors) => replaceKoiError(errors, 'any.only', 'IDENTIFIER_DECLARATION_EXISTS')),
    }),
    identifierRegistrationKp: Koi.when('...matchingResultCode', {
        is: Koi.valid(MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION),
        otherwise: identifierRegistrationKpSchema,
        then: Koi.valid(null).error((errors) =>
            replaceKoiError(errors, 'any.only', 'IDENTIFIER_REGISTRATION_KP_EXISTS')
        ),
    }),
});

export const practitionerEmploymentFrontendSchema: ISchemaContainer<PractitionerEmploymentAddValidated> = {
    schema: Koi.object().keys({
        employment: employmentAdd.concat(Koi.object().keys({ emailAddress: emailSchema })),
        organizationId: Koi.string().uuid().required(),
        organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
        practitionerRegistrationCodes: practitionerRegistrationCodesFrontend.error((errors) =>
            replaceErrorPath(errors, 'practitionerRegistrationCodes', 'practitioner')
        ),
        practitionerWillUseCS: Koi.boolean().required(),
    }),
};

export const practitionerEmploymentBackendSchema: ISchemaContainer<PractitionerEmploymentAddValidated> = {
    schema: Koi.object()
        .keys({
            employment: employmentAdd.concat(
                Koi.object().keys({
                    emailAddress: Koi.when('/practitionerWillUseCS', {
                        is: true,
                        otherwise: Koi.string().empty().allow('', null),
                        then: emailSchema.allow(null),
                    }),
                })
            ),
            organizationId: Koi.string().uuid().required(),
            organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
            practitionerRegistrationCodes: practitionerRegistrationCodesBackend.error((errors) =>
                replaceErrorPath(errors, 'practitionerRegistrationCodes', 'practitioner')
            ),
            practitionerWillUseCS: Koi.boolean().required(),
        })
        .unknown(true),
};

export const organizationEmploymentAddFrontendSchema: ISchemaContainer<OrganizationEmploymentAddFrontendValidated> = {
    schema: Koi.object().keys({
        employment: employmentAdd.concat(
            Koi.object().keys({
                emailAddress: Koi.when('/practitionerWillUseCS', {
                    is: true,
                    otherwise: Koi.string().empty().allow('', null),
                    then: Koi.when('/matchingPractitionerHasEmailAddress', {
                        is: true,
                        otherwise: emailSchema.required(),
                        then: emailSchema.allow(null),
                    }),
                }),
            })
        ),
        matchingAccountRequestId: Koi.string().uuid().allow(null),
        matchingPractitionerHasEmailAddress: Koi.boolean().allow(null).required(),
        matchingResultCode: Koi.koi().enum(MatchingEntityResultCode).required(),
        organizationId: Koi.string().uuid().required(),
        organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
        practitionerId: Koi.string().uuid().allow(null),
        practitionerRegistrationCodes: optionalPractitionerRegistrationCodes.concat(
            duplicatedIdentifierDeclarationAvoidanceSchema
        ),
        practitionerWillUseCS: Koi.boolean().required(),
    }),
};

export const organizationPractitionerAddBackendSchema: ISchemaContainer<OrganizationPractitionerAddBackendValidated> = {
    schema: Koi.object()
        .keys({
            employment: employmentAdd,
            matchingAccountRequestId: Koi.string().uuid().allow(null),
            organizationType: Koi.koi().enum(OrganizationTypeWithLinkedProfessions).allow(null),
            practitioner: practitionerAddBackend,
            practitionerWillUseCS: Koi.boolean(),
        })
        .unknown(true),
};

export interface PractitionerLanguageValidated {
    language: string;
}

export const practitionerUpdateLanguageSchema: ISchemaContainer<PractitionerLanguageValidated> = {
    schema: Koi.object().keys({ language: Koi.koi().enum(LanguageOptions) }),
};

const thirdPartySsoLoginCredentials = Koi.object({
    issuer: Koi.koi().enum(ThirdPartySsoName).optional(),
    ssoToken: Koi.string().optional(),
});

const extraLoginParameters = Koi.object({
    episodeCategory: Koi.koi().enum(EpisodeCategory).optional(),
    organizationId: Koi.string().uuid().optional(),
    patientId: Koi.string().uuid().optional(),
    requestId: Koi.string().uuid().optional(),
});

export interface ILoginCredentialsValidated {
    password: string;
    userName: string;
}
const loginCredentials = Koi.object({
    password: Koi.string().max(255),
    userName: Koi.string().max(255),
});
export const loginCredentialsSchema: ISchemaContainer<ILoginCredentialsValidated> = {
    schema: loginCredentials
        .concat(thirdPartySsoLoginCredentials)
        .concat(extraLoginParameters)
        .options({ convert: false, presence: 'required' }),
};

export interface ILoginFullValidated extends ILoginCredentialsValidated {
    episodeCategory?: string;
    issuer?: ThirdPartySsoName;
    organizationId?: string;
    patientId?: string;
    requestId?: string;
    ssoToken?: string;
    totpToken: string;
}
export const loginFullSchema: ISchemaContainer<ILoginFullValidated> = {
    schema: loginCredentials
        .keys({
            totpToken: Koi.string().totpToken().optional(),
        })
        .concat(thirdPartySsoLoginCredentials)
        .concat(extraLoginParameters)
        .options({ convert: false, presence: 'required' }),
};

interface IRegistrationUserNameAndCodeValidated {
    registrationCode: string;
    userName: string;
}
const registrationUserNameAndCode = Koi.object({
    registrationCode: Koi.string().registrationCode(),
    userName: Koi.string().max(255),
});

interface IRegistrationPasswordValidated extends IRegistrationUserNameAndCodeValidated {
    newPassword: string;
    newPasswordRepeat: string;
}
const registrationPassword = registrationUserNameAndCode.keys({
    newPassword: Koi.string().max(255),
    newPasswordRepeat: Koi.string().newPasswordRepeat(),
});

interface IRegistrationTotpValidated extends IRegistrationPasswordValidated {
    totpToken: string;
}
const registrationTotp = registrationPassword.keys({
    totpToken: Koi.string().totpToken(),
});

export interface IRegistrationFullValidated extends IRegistrationTotpValidated {
    otpSecret: string;
}
export const registrationFullSchema: ISchemaContainer<IRegistrationFullValidated> = {
    schema: registrationTotp
        .keys({
            newPassword: Koi.string().min(10).required(),
            otpSecret: Koi.string()
                .regex(/[0-9A-Z]{52}/)
                .max(52),
        })
        .options({ convert: false, presence: 'required' }),
};

export const practitionerEmailOnlySchema = {
    schema: Koi.object({
        emailAddress: emailSchema,
    }).options({ convert: false, presence: 'required' }),
};

export interface IKsyosProfileDetailsValidated {
    emailAddress: string;
    gender: Gender;
    mobileNumber: string | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

const ksyosProfileDetails = Koi.object().keys({
    emailAddress: emailSchema,
    gender: Koi.koi().enum(Gender),
    mobileNumber: phoneNumberSchema.allow(null),
    nameFamily: Koi.string().max(255),
    nameGiven: Koi.string().max(255).allow(null),
    nameInfix: Koi.string().max(255).allow(null),
});

export const ksyosProfileDetailsSchema: ISchemaContainer<IKsyosProfileDetailsValidated> = {
    schema: ksyosProfileDetails.options({ convert: false, presence: 'required' }),
};

const consumerRolFilterValidation = Koi.object().keys({
    serviceCode: Koi.koi().enum(ServiceCode),
    serviceTypes: Koi.array().items(Koi.koi().enum(ServiceType)),
});

interface IConsumerRolFilter {
    serviceCode?: ServiceCode;
    serviceTypes?: ServiceType[];
}

const providerRolFilterValidation = Koi.object().keys({
    isAnalist: Koi.boolean().disallow(false).allow(null),
    isBiometrician: Koi.boolean().disallow(false).allow(null),
    isGrader: Koi.boolean().disallow(false).allow(null),
    serviceTypes: Koi.array().items(Koi.koi().enum(ServiceType)),
});

interface IProviderRolFilter {
    isAnalist?: boolean;
    isBiometrician?: boolean;
    isGrader?: boolean;
    serviceTypes: ServiceType[];
}

const rolFilterValidation = Koi.object().keys({
    consumer: consumerRolFilterValidation,
    provider: providerRolFilterValidation,
    reader: Koi.boolean().allow(null),
    supervisor: Koi.boolean().allow(null),
});

export interface IPractitionerRolFilter {
    consumer?: IConsumerRolFilter;
    provider?: IProviderRolFilter;
    reader?: boolean;
    supervisor?: boolean;
}

export const practitionerFilterValidation = Koi.object({
    city: Koi.string().max(255).allow(null),
    emailAddress: Koi.string().max(255).allow(null),
    episodeCategoryIds: Koi.array().items(Koi.string().uuid()).allow(null),
    excludeId: Koi.string().uuid().allow(null),
    gender: Koi.koi().enum(Gender).allow(null),
    hasActiveEmployment: Koi.boolean().allow(null),
    hasNoActiveEmployment: Koi.boolean().allow(null),
    hasNoActiveKsyosEmployment: Koi.boolean().allow(null),
    identifierDeclaration: Koi.string().max(255).allow(null),
    identifierRegistrationBig: Koi.string().max(255).allow(null),
    identifierRegistrationKp: Koi.string().max(255).allow(null),
    isActivated: Koi.boolean().allow(null),
    name: Koi.string().max(255).allow(null, ''),
    nameFamily: Koi.string().max(255).allow(null),
    nameGivenFirstLetter: Koi.string().max(1).allow(null),
    nameOrId: Koi.string().max(255).allow(null),
    notActiveInOrganizationId: Koi.string().uuid().allow(null),
    organizationId: Koi.string().uuid().allow(null),
    relationNumber: Koi.string().max(255).allow(null),
    roles: rolFilterValidation.allow(null),
    searchText: Koi.string().max(255).allow(null, ''),
    selectedProfessions: professions.allow(null),
    serviceGroupIds: Koi.array().items(Koi.string().uuid()).allow(null),
    status: Koi.koi().enum(ActiveOrInactive).allow(null),
    uid: Koi.number().min(1).max(100000000000).allow(null),
    userName: Koi.string().max(255).allow(null),
    zorgmailNummer: Koi.string().allow(null),
});

export interface IPractitionerFilters {
    city?: string | null;
    emailAddress?: string | null;
    episodeCategoryIds?: string[] | null;
    excludeId?: string | null;
    gender?: string | null;
    hasActiveEmployment?: boolean | null;
    hasNoActiveEmployment?: boolean | null;
    hasNoActiveKsyosEmployment?: boolean | null;
    identifierDeclaration?: string | null;
    identifierRegistrationBig?: string | null;
    identifierRegistrationKp?: string | null;
    isActivated?: boolean | null;
    name?: string | null;
    nameFamily?: string | null;
    nameGivenFirstLetter?: string | null;
    notActiveInOrganizationId?: string | null;
    organizationId?: string | null;
    relationNumber?: string | null;
    roles?: IPractitionerRolFilter | null;
    searchText?: string | null;
    selectedProfessions?: Profession[] | null;
    serviceGroupIds?: string[] | null;
    uid?: number | null;
    userName?: string | null;
    zorgmailNummer?: string | null;
}

export const practitionerExportColumnsValidation = Koi.object({
    bankAccountNumber: Koi.boolean(),
    bankAccountStatus: Koi.boolean(),
    bankAccountSubscriber: Koi.boolean(),
    emailAddress: Koi.boolean(),
    employments: Koi.boolean(),
    financialEmailAddress: Koi.boolean(),
    id: Koi.boolean(),
    identifierDeclaration: Koi.boolean(),
    identifierRegistrationBig: Koi.boolean(),
    identifierRegistrationKp: Koi.boolean(),
    isActivated: Koi.boolean(),
    lastActivityDate: Koi.boolean(),
    mobileNumber: Koi.boolean(),
    name: Koi.boolean(),
    organizations: Koi.boolean(),
    relationNumber: relationNumberExistsSchema,
    service: Koi.boolean(),
    zorgmailNummer: Koi.boolean(),
});

export interface IKsyosAccountDetailsValidated {
    startDate: string;
    userName: string;
}

const ksyosAccountDetails = Koi.object().keys({
    profilePictureDataUrl: Koi.string()
        .max(calculateBase64Length(10 * 1024 * 1024) + 100)
        .optional(),
    startDate: Koi.koi().date().todayOrAfter(),
    userName: practitionerUserNameValidation,
});

export const ksyosAccountDetailsSchema: ISchemaContainer<IKsyosAccountDetailsValidated> = {
    schema: ksyosAccountDetails.options({ convert: false, presence: 'required' }),
};
